/**
 * GA4 identity helpers.
 *
 * Reads the GA4 client_id and session_id from the browser cookies set by
 * gtag.js. These values are required to fire server-side Measurement Protocol
 * events (from our Supabase edge functions) that correctly attribute to the
 * same GA4 user and session as client-side events. Without them, server-side
 * events create phantom users in GA4.
 *
 * Cookie formats (as documented at
 * https://developers.google.com/analytics/devguides/collection/ga4/cookie-usage):
 *
 *   _ga                    = GA1.<domain_depth>.<client_id>.<timestamp>
 *     e.g. "GA1.1.1234567890.9876543210"
 *     The client_id portion is "1234567890.9876543210".
 *
 *   _ga_<measurement_id>   = GA's session-state cookie. Google has changed
 *     this value format over time, so this helper accepts both the older
 *     dot-delimited form (e.g. "GS1.1.1712345678.3...") and the newer
 *     tagged form (e.g. "GS2.1.s1712345678$o3...").
 *
 * Both cookies are set by gtag.js when a GA4 measurement tag fires. If GA4 is
 * blocked (ad blocker, consent denied) these cookies do not exist and the
 * helpers return null, which the caller should handle gracefully.
 */

/**
 * Extract the GA4 client_id from the _ga cookie.
 *
 * @returns The client_id string (e.g. "1234567890.9876543210") or null if
 *   the cookie is missing, malformed, or we're running in a non-browser
 *   context (e.g. SSR, native).
 */
export function getGA4ClientId(): string | null {
  if (typeof document === 'undefined') return null;
  // Match GA1.<digits>.<digits>.<digits> — the _ga cookie is domain-scoped.
  // The client_id is the trailing "<digits>.<digits>" portion.
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/);
  return match ? match[1] : null;
}

/**
 * Extract the GA4 session_id from the _ga_<measurement_id> cookie.
 *
 * @param measurementId - The GA4 measurement ID (e.g. "G-2DG1FGSNBM").
 *   If provided, we target that specific cookie. If omitted, we match any
 *   _ga_<suffix> cookie (useful when the measurement ID is not yet loaded
 *   into memory).
 * @returns The session_id string or null if the cookie is missing.
 */
export function getGA4SessionId(measurementId?: string | null): string | null {
  if (typeof document === 'undefined') return null;

  const suffix = measurementId?.replace(/^G-/, '');
  const re = suffix
    ? new RegExp(`(?:^|;\\s*)_ga_${suffix}=([^;]+)`)
    : /(?:^|;\s*)_ga_[A-Z0-9]+=([^;]+)/;

  const match = document.cookie.match(re);
  const value = match?.[1];
  if (!value) return null;

  const taggedSessionMatch = value.match(/(?:^|[.$])s(\d+)(?:[$.]|$)/);
  if (taggedSessionMatch) return taggedSessionMatch[1];

  const dotDelimitedSessionMatch = value.match(/^GS\d+\.\d+\.(\d+)/);
  return dotDelimitedSessionMatch ? dotDelimitedSessionMatch[1] : null;
}

/**
 * Convenience: read both identifiers in one call.
 * Returns an object with both fields (either may be null).
 */
export function getGA4Identity(measurementId?: string | null): {
  gaClientId: string | null;
  gaSessionId: string | null;
} {
  return {
    gaClientId: getGA4ClientId(),
    gaSessionId: getGA4SessionId(measurementId),
  };
}
